<template>
  <div id="app-wrapper">
    <router-view/>
    <AdPopup v-if="showAdPopup && !isYouthProtectionLocked" @close="closeAdPopup" />
    <YouthProtectionLock v-if="isYouthProtectionLocked" :used-ms="youthProtectionUsedMs" />
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import AdPopup from '@/components/AdPopup.vue';
import YouthProtectionLock from '@/components/YouthProtectionLock.vue';
import { isVipUser, isLoggedIn } from '@/api/user';
import {
  getYouthProtectionStatus,
  setYouthProtectionLastActiveAt,
  settleYouthProtectionUsage
} from '@/utils/youthProtection';

export default {
  name: 'App',
  components: {
    AdPopup,
    YouthProtectionLock
  },
  setup() {
    const router = useRouter();
    const showAdPopup = ref(false);
    const lastRoute = ref('');
    const isYouthProtectionLocked = ref(false);
    const youthProtectionUsedMs = ref(0);
    let youthProtectionTimer = null;

    const AD_ROUTES = ['Home', 'Category', 'ComicDetail'];

    const updateYouthProtectionState = () => {
      const status = settleYouthProtectionUsage();
      isYouthProtectionLocked.value = status.isLocked;
      youthProtectionUsedMs.value = status.usedMs;

      if (status.isLocked) {
        showAdPopup.value = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        settleYouthProtectionUsage({ force: true });
        return;
      }

      setYouthProtectionLastActiveAt();
      updateYouthProtectionState();
    };

    const startYouthProtectionTimer = () => {
      updateYouthProtectionState();
      youthProtectionTimer = setInterval(updateYouthProtectionState, 1000);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    };

    const stopYouthProtectionTimer = () => {
      if (youthProtectionTimer) {
        clearInterval(youthProtectionTimer);
        youthProtectionTimer = null;
      }

      settleYouthProtectionUsage({ force: true });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    const shouldShowAd = () => {
      if (!isLoggedIn()) {
        return true;
      }

      if (isVipUser()) {
        return false;
      }

      return true;
    };

    const checkAndShowAd = (toRoute, fromRoute) => {
      const toName = toRoute?.name;
      const fromName = fromRoute?.name;

      if (isYouthProtectionLocked.value) {
        return;
      }

      if (!AD_ROUTES.includes(toName)) {
        return;
      }

      if (!fromName || !AD_ROUTES.includes(fromName)) {
        return;
      }

      if (toName === fromName) {
        return;
      }

      if (shouldShowAd()) {
        showAdPopup.value = true;
      }
    };

    const closeAdPopup = () => {
      showAdPopup.value = false;
    };

    watch(
      () => router.currentRoute.value,
      (to, from) => {
        updateYouthProtectionState();
        checkAndShowAd(to, from);
      }
    );

    onMounted(() => {
      lastRoute.value = router.currentRoute.value.name;
      const status = getYouthProtectionStatus();
      youthProtectionUsedMs.value = status.usedMs;
      isYouthProtectionLocked.value = status.isLocked;
      setYouthProtectionLastActiveAt();
      startYouthProtectionTimer();
    });

    onBeforeUnmount(() => {
      stopYouthProtectionTimer();
    });

    return {
      showAdPopup,
      closeAdPopup,
      isYouthProtectionLocked,
      youthProtectionUsedMs
    };
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #app {
  height: 100%;
  width: 100%;
  overflow: auto;
}

#app {
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
